import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { BookOpen, Users, HelpCircle, CheckCircle } from 'lucide-react';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding: 1rem 2rem;
  
  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.8rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  font-size: 1.5rem;
  font-weight: 600;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    font-size: 1rem;
    gap: 6px;
  }
`;

const ProgressContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 1024px) {
    gap: 10px;
  }
  
  @media (max-width: 768px) {
    gap: 8px;
    flex-wrap: wrap;
  }
  
  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
    gap: 5px;
  }
`;

const ProgressStep = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 20px;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.active ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.2)'};
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.8rem;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    padding: 4px 8px;
    font-size: 0.7rem;
    gap: 4px;
  }
`;

const ProgressLine = styled.div`
  width: 2px;
  height: 20px;
  background: rgba(255, 255, 255, 0.3);
  margin: 0 10px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: white;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    gap: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 0.7rem;
    width: 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => 
    props.status === 'completed' ? '#4ade80' :
    props.status === 'active' ? '#fbbf24' :
    props.status === 'waiting' ? '#60a5fa' :
    'rgba(255, 255, 255, 0.3)'
  };
  animation: ${props => props.status === 'active' ? 'pulse 2s infinite' : 'none'};
`;

const Header = ({ currentStage, status, completedStages = [] }) => {
  const steps = [
    { id: 'story', label: 'Story', icon: BookOpen },
    { id: 'dialogues', label: '', icon: Users },
    { id: 'questions', label: 'Questions', icon: HelpCircle },
    { id: 'completed', label: 'Complete', icon: CheckCircle }
  ];

  const getStepStatus = (stepId) => {
    if (completedStages && completedStages.has(stepId)) return 'completed';
    if (stepId === currentStage) return 'active';
    return 'waiting';
  };

  return (
    <HeaderContainer>
      <HeaderContent>
        <Logo
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <BookOpen size={28} />
          Storytelling Game
        </Logo>

        <ProgressContainer>
          {steps.map((step, index) => {
            const stepStatus = getStepStatus(step.id);
            const Icon = step.icon;
            
            return (
              <React.Fragment key={step.id}>
                <ProgressStep
                  active={stepStatus === 'active'}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Icon size={16} />
                  {step.label}
                </ProgressStep>
                {index < steps.length - 1 && <ProgressLine />}
              </React.Fragment>
            );
          })}
        </ProgressContainer>

        <StatusIndicator>
          <StatusDot status={status === 'waiting_approval' ? 'active' : 'completed'} />
          {status === 'waiting_approval' ? 'Waiting for approval' : 'Ready'}
        </StatusIndicator>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;


